import * as peopleSelectors from "../people/peopleSelectors";
import * as numbers from "common/numbers";
import { extractManualSovPricing } from "domain/manualSovPricing";
import { createSelector, createStructuredSelector } from "reselect";
import * as SOV from "store/SOV";
import * as analytics from "store/analytics";
import * as baseLayerBurn from "store/baseLayerBurn";
import * as catExposures from "store/catExposures";
import * as endorsements from "store/endorsements";
import * as glClassifications from "store/glClassifications";
import * as glExposures from "store/glExposures";
import * as layerDefaults from "store/layerDefaults";
import * as lossDevelopmentStore from "store/lossDevelopment";
import { selectModelData } from "store/modelDataStore";
import * as programPricing from "store/programPricing";
import * as rateChangeStore from "store/rateChange";
import * as rulesStore from "store/rules";
import * as exposureSelectors from "store/selectors/input/exposure/exposureSelectors";
import * as flagsSelectors from "store/selectors/input/flags/flagsSelectors";
import * as programSelectors from "store/selectors/input/program/programSelectors";
import * as peersSelectors from "store/selectors/peers/peersSelectors";
import {
  selectSubmissionId,
  selectFilename,
} from "store/selectors/persistence/persistenceSelectors";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";
import * as towerPricingSelectors from "store/selectors/pricing/tower/towerPricingSelectors";

// In some cases, the Analytics schema module is very large. This can make the resolved
// submission too large to return from a Lambda. For now, we just need a few values out
// of this in the data warehouse. So only return those.
//
// Per-location SOV pricing is one entry per location, so an uploaded SOV can make it
// huge. Keep only the manual locations, for the Manual Locations report; the Property
// SOV page saves a single location, so that is one entry. A saved
// submission has values.sov offloaded to a file, with the manual entries kept
// inline as values.manualSov (see cleanStateForPersistence); live pricing has sov.
const selectRestrictedAnalytics = createSelector(
  [analytics.select],
  (analytics) => {
    if (!analytics?.programPricing?.values) {
      return null;
    }
    const { sov, manualSov } = analytics.programPricing.values;
    const manualSovPricing = sov
      ? extractManualSovPricing(sov)
      : extractManualSovPricing(manualSov);
    return {
      programPricing: {
        ...analytics?.programPricing,
        values: {
          sovSummary: analytics.programPricing.values.sovSummary,
          layers: analytics.programPricing.values.layers,
          ...(Object.keys(manualSovPricing).length
            ? { sov: manualSovPricing }
            : {}),
        },
      },
    };
  }
);

const selectPredominantStateFromExposures = createSelector(
  [glExposures.selectResolvedGlExposures],
  (exposures) => {
    if (!exposures?.length) return null;
    const counts = {};
    const totals = {};
    for (const row of exposures) {
      if (!row.state) continue;
      counts[row.state] = (counts[row.state] ?? 0) + 1;
      totals[row.state] =
        (totals[row.state] ?? 0) + (parseFloat(row.exposure_amount) || 0);
    }
    let best = null,
      bestCount = 0,
      bestTotal = 0;
    for (const [state, count] of Object.entries(counts)) {
      if (
        count > bestCount ||
        (count === bestCount && totals[state] > bestTotal)
      ) {
        best = state;
        bestCount = count;
        bestTotal = totals[state];
      }
    }
    return best;
  }
);

const selectGlLimits = createSelector(
  [
    pricingSelectors.getActiveLayer,
    selectPredominantStateFromExposures,
    programSelectors.getExperienceRating,
    programSelectors.getExperienceRatingResult,
    flagsSelectors.selectPrimaryGlRatingLimits,
  ],
  (
    layer,
    exposurePredominantState,
    experienceRating,
    experienceResult,
    primaryLimits
  ) => {
    // Legacy submissions may hold strings in numeric layer fields; coerce
    // every scalar numeric field so glLimits is fully numeric.
    const num = (value) => numbers.parseNumberString(value);
    // GL Pricing dropdown sentinels are explicit underwriter choices: they must
    // NOT fall back to the flag-derived value (picking "Excluded" must not
    // resurrect a limit from the flags). Rating-critical fields (aggregates +
    // liquor limits) pass the sentinel VERBATIM so the engine can distinguish
    // "excluded" from "unset" (null liquor limits default to the occurrence
    // limit in the engine); the other limits resolve sentinels to null.
    const isLimitSentinel = (value) =>
      value === "Excluded" || value === "Not Covered" || value === "Included";
    const limit = (layerValue, fallback) =>
      isLimitSentinel(layerValue) ? null : num(layerValue) ?? fallback ?? null;
    const limitOrSentinel = (layerValue, fallback) =>
      isLimitSentinel(layerValue)
        ? layerValue
        : num(layerValue) ?? fallback ?? null;
    return {
      // Primary GL limits come from the Primary GL Rating dropdowns (gl* is the
      // GL-Pricing-screen override). Never fall back to layer.limit — that's the
      // layer's structural limit (the excess limit for an excess layer).
      occurrence_limit: limit(
        layer?.glOccurrenceLimit,
        primaryLimits?.occurrenceLimit
      ),
      general_aggregate: limitOrSentinel(
        layer?.glGeneralAggregate,
        primaryLimits?.generalAggregate
      ),
      pco_aggregate: limitOrSentinel(
        layer?.glPcoAggregate,
        primaryLimits?.pcoAggregate
      ),
      liquor_occurrence_limit: limitOrSentinel(
        layer?.liquorOccurrenceLimit,
        primaryLimits?.liquorOccurrenceLimit
      ),
      liquor_aggregate_limit: limitOrSentinel(
        layer?.liquorAggregateLimit,
        primaryLimits?.liquorAggregateLimit
      ),
      deductible_amount: limit(
        layer?.deductibleAmount,
        primaryLimits?.deductibleAmount
      ),
      deductible_type: layer?.deductibleType ?? "Per Occurrence",
      predominant_state:
        layer?.predominantState ?? exposurePredominantState ?? null,
      // Additional GL limits — Primary Rating B14/B22/B23
      personal_advertising_injury_limit: limit(
        layer?.personalAndAdvertisingInjuryLimit,
        primaryLimits?.personalAndAdvertisingInjuryLimit
      ),
      damage_to_premises_limit: limit(
        layer?.damageToPremisesLimit,
        primaryLimits?.damageToPremisesLimit
      ),
      medical_expense_limit: limit(
        layer?.medicalExpenseLimit,
        primaryLimits?.medicalExpenseLimit
      ),
      // Schedule-of-coverage limits entered on GL Pricing (dropdowns; a value may
      // be a number or a sentinel string). Not used in rating; carried for
      // document generation and display.
      hnoa_limit: layer?.hnoaLimit ?? null,
      employee_benefits_each_employee_limit:
        layer?.employeeBenefitsEachEmployeeLimit ?? null,
      employee_benefits_aggregate_limit:
        layer?.employeeBenefitsAggregateLimit ?? null,
      assault_battery_each_event_limit:
        layer?.assaultBatteryEachEventLimit ?? null,
      assault_battery_aggregate_limit:
        layer?.assaultBatteryAggregateLimit ?? null,
      // Per state+class territory-factor overrides entered on GL Pricing, keyed
      // by `${state}${class_code}`; engine clamps each to the class's range.
      territory_factors: layer?.glTerritoryFactors ?? {},
      // Per state+class ELP/RTC loss-cost overrides entered on GL Pricing, keyed
      // by `${state}${class_code}`; engine applies them only to ELP/RTC classes.
      prem_ops_loss_cost_overrides: layer?.glPremOpsLossCostOverrides ?? {},
      pco_loss_cost_overrides: layer?.glPcoLossCostOverrides ?? {},
      // Experience mod (loss rating): applied to PremOps/PCO/Liquor premiums when
      // the UW toggles it on and the risk qualifies. Mirrors workbook B72/B70.
      apply_experience_mod: experienceRating?.applyExperienceMod ?? false,
      experience_mod: experienceResult?.qualifies
        ? num(experienceResult?.experience_mod) ?? 0
        : 0,
      // Renewable Y/N — Primary Rating E22
      renewable: layer?.renewable ?? null,
      // Coverage Enhancements / Stop Gap — additional premium add-ons
      coverage_enhancements_pct: num(layer?.coverageEnhancementsPct) ?? 0,
      stop_gap_payroll: num(layer?.stopGapPayroll) ?? 0,
      // Stop Gap Limit — Primary Rating I5
      stop_gap_limit: num(layer?.stopGapLimit) ?? null,
      // HNOA — engine auto-computes from employees + cost-of-hire when present;
      // hnoaPremium override (if set) wins.
      hnoa_employees: num(layer?.hnoaEmployees) ?? 0,
      hnoa_cost_of_hire: num(layer?.hnoaCostOfHire) ?? 0,
      hnoa_premium: num(layer?.hnoaPremium) ?? null,
      // IRPM as a 7-line breakdown; engine sums and floors at -50%
      irpm: {
        condition_and_care_of_premises: num(layer?.irpmConditionCare) ?? 0,
        type_condition_and_care_of_equipment:
          num(layer?.irpmTypeCondition) ?? 0,
        selection_training_supervision_and_experience:
          num(layer?.irpmSelectionTraining) ?? 0,
        safety_program: num(layer?.irpmSafetyProgram) ?? 0,
        peculiarities_of_classification: num(layer?.irpmPeculiarities) ?? 0,
        terms_and_conditions: num(layer?.irpmTermsConditions) ?? 0,
        unique_risk_not_otherwise_contemplated: num(layer?.irpmUniqueRisk) ?? 0,
      },
      manual_premium_adjustment: num(layer?.manualPremiumAdjustment) ?? 0,
      tria_percent: num(layer?.triaPercent) ?? 0,
      // commissionPct (decimal) overrides; fall back to layer.brokerage (integer %)
      commission_pct: (() => {
        const commissionPct = num(layer?.commissionPct);
        if (commissionPct != null) return commissionPct;
        const brokerage = num(layer?.brokerage);
        return brokerage != null ? brokerage / 100 : 0;
      })(),
    };
  }
);

export const selectResolvedSubmissionWithoutInput = createStructuredSelector({
  submissionId: selectSubmissionId,
  program: programSelectors.selectResolvedProgram,
  pricingInputs: pricingSelectors.selectResolvedPricingInputs,
  layers: pricingSelectors.selectResolvedLayers,
  policies: pricingSelectors.getPolicies,
  tower: towerPricingSelectors.selectResolvedTower,
  scheduleOfUnderlying: towerPricingSelectors.getScheduleOfUnderlyingLayers,
  claimsQuery: pricingSelectors.selectPricingClaims,
  rateChange: rateChangeStore.selectRateChange,
  layerDefaults: layerDefaults.select,
  lossDevelopment: lossDevelopmentStore.selectLossDevelopment,
  baseLayerBurn: baseLayerBurn.selectBaseLayerBurn,
  exposure: exposureSelectors.selectResolvedExposure,
  peers: peersSelectors.selectResolvedPeers,
  sov: SOV.selectSOV,
  programPricing: programPricing.select,
  modelData: selectModelData,
  flags: flagsSelectors.selectResolvedFlags,
  people: peopleSelectors.selectResolvedPeople,
  catExposures: catExposures.select,
  endorsements: endorsements.select,
  rules: rulesStore.select,
  analytics: selectRestrictedAnalytics,
  glExposures: glExposures.selectResolvedGlExposures,
  glClassifications: glClassifications.selectResolvedGlClassifications,
  glLimits: selectGlLimits,
  autoExposureRating: programSelectors.getAutoExposureRating,
  lossRating: programSelectors.getLossRating,
  autoLossRating: programSelectors.getAutoLossRating,
  xsRating: programSelectors.getXsRating,
});

export const selectResolvedSubmission = createSelector(
  [selectResolvedSubmissionWithoutInput, (state) => state, selectFilename],
  (resolvedSubmission, state, filename) => {
    return {
      ...resolvedSubmission,
      submission: {
        state: Object.fromEntries(
          Object.entries(state ?? {}).filter(
            ([key]) => key !== "temp" && !String(key).endsWith("Api")
          )
        ),
        filename,
      },
    };
  }
);
